/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  LitElement,
  html,
  property,
  CSSResultArray,
  TemplateResult,
} from 'lit-element';
import { customElement, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styles } from './nile-option.css';

/**
 * Nile icon component.
 *
 * @tag nile-option
 *
 */
@customElement('nile-option')
export class NileOption extends LitElement {
  /**
   * The styles for Option
   * @remarks If you are extending this class you can extend the base styles with super. Eg `return [super(), myCustomStyles]`
   */
  public static get styles(): CSSResultArray {
    return [styles];
  }

  /* #endregion */

  @state() current = false; // the user has keyed into the option, but hasn't selected it yet (shows a highlight)
  @state() selected = false; // the option is selected and has aria-selected="true"
  @state() hidden = false; // the option is hidden
  @state() hasHover = false; // we need this because Safari doesn't honor :hover styles while dragging

  /**
   * The option's value. When selected, the containing form control will receive this value. The value must be unique
   * from other options in the same group. Values may not contain spaces, as spaces are used as delimiters when listing
   * multiple values.
   */
  @property({ reflect: true }) value = '';

  /** Draws the option in a disabled state, preventing selection. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Draws the option in a disabled state, preventing selection. */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /* #region Methods */

  private handleDefaultSlotChange() {
    // const textLabel = this.getTextLabel();
    // // Ignore the first time the label is set
    // if (typeof this.cachedTextLabel === 'undefined') {
    //   this.cachedTextLabel = textLabel;
    //   return;
    // }
    // When the label changes, emit a slotchange event so parent controls see it
    // if (textLabel !== this.cachedTextLabel) {
    //   this.cachedTextLabel = textLabel;
    //   this.emit('slotchange', { bubbles: true, composed: false, cancelable: false });
    // }
  }

  /** Returns a plain text label based on the option's content. */
  getTextLabel(): string {
    return (this.textContent ?? '').trim();
  }

  onMouseEnter(): void {
    this.hasHover = true;
  }

  onMouseLeave(): void {
    this.hasHover = false;
  }
  /**
   * Render method
   * @slot This is a slot test
   */
  public render(): TemplateResult {
    return html`
      ${!this.hidden
        ? html`
            <div
              class="option ${this.multiple ? 'extra-padding' : ''}"
              @mouseenter=${this.onMouseEnter}
              @mouseleave=${this.onMouseLeave}
            >
              ${this.multiple
                ? html`<input
                    type="checkbox"
                    style="pointer-events: none;"
                    ?checked=${this.selected}
                  />`
                : ''}

              <span class="label-container">
                <span class="prefix">
                  <slot part="prefix" name="prefix"></slot>
                </span>

                <slot
                  part="label"
                  class="label"
                  @slotchange=${this.handleDefaultSlotChange}
                ></slot>
                <slot part="suffix" name="suffix" class="option__suffix"></slot>
              </span>
              ${this.selected && !this.multiple
                ? html`<nile-icon name="tick" class="check"> </nile-icon>`
                : ''}
            </div>
          `
        : ''}
    `;
  }

  // class=${classMap({
  //   option: true,
  //   'option--current': this.current,
  //   'option--disabled': this.disabled,
  //   'option--selected': this.selected,
  //   'option--hover': this.hasHover
  // })}
  /* #endregion */
}

export default NileOption;

declare global {
  interface HTMLElementTagNameMap {
    'nile-option': NileOption;
  }
}
